/**
 * ข้อมูลหลักที่ทั้งแอปใช้ร่วมกัน
 *
 * โหลดครั้งเดียวตอนเข้าระบบแล้วเก็บไว้ในหน่วยความจำ — สินค้าและราคาไม่เปลี่ยน
 * ระหว่างวัน การยิงถามใหม่ทุกครั้งที่เปิดหน้าจอทำให้กดแล้วรอ ซึ่งหน้าร้านรับไม่ได้
 *
 * ★ ยอดเงินไม่เคยถูกคำนวณที่นี่ ★ ทุกยอดมาจาก API เสมอ
 * หน้าจอมีหน้าที่แสดงผลอย่างเดียว — เป็นเหตุผลที่ระบบเดิมยอดไม่ตรงกัน 488 บิล
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, getSession, onSessionChange, type Session } from './lib/api.ts';
import type {
  Branch,
  DiscountButton,
  DeliveryChannel,
  DiningTable,
  DrawerSession,
  ExpenseCategory,
  Kitchen,
  PaymentMethod,
  Printer,
  Product,
  ProductGroup,
} from './lib/types.ts';

const BRANCH_KEY = 'sushimai.branchId';

export interface Store {
  session: Session;
  branch: Branch;
  branches: Branch[];
  products: Product[];
  productGroups: ProductGroup[];
  /** ปุ่มส่วนลดที่อยู่ในกริดเหมือนสินค้า — ของเดิมคือ prod_type 8/9 */
  discountButtons: DiscountButton[];
  paymentMethods: PaymentMethod[];
  deliveryChannels: DeliveryChannel[];
  tables: DiningTable[];
  kitchens: Kitchen[];
  printers: Printer[];
  expenseCategories: ExpenseCategory[];
  drawer: DrawerSession | null;
  reloadDrawer: () => Promise<void>;
  reload: () => Promise<void>;
}

const StoreContext = createContext<Store | null>(null);

export function useStore(): Store {
  const store = useContext(StoreContext);
  if (!store) throw new Error('ใช้ useStore นอก StoreProvider ไม่ได้');
  return store;
}

/** สาขาที่เครื่องนี้ทำงานอยู่ — พนักงานถูกกำหนดมาแล้ว เจ้าของเลือกเอง */
export function selectedBranchId(session: Session): number | null {
  if (session.user.branchId !== null) return session.user.branchId;
  const stored = Number(localStorage.getItem(BRANCH_KEY));
  return Number.isInteger(stored) && stored > 0 ? stored : null;
}

export function setSelectedBranchId(id: number): void {
  localStorage.setItem(BRANCH_KEY, String(id));
}

interface LoadedData {
  branches: Branch[];
  products: Product[];
  productGroups: ProductGroup[];
  /** ปุ่มส่วนลดที่อยู่ในกริดเหมือนสินค้า — ของเดิมคือ prod_type 8/9 */
  discountButtons: DiscountButton[];
  paymentMethods: PaymentMethod[];
  deliveryChannels: DeliveryChannel[];
  tables: DiningTable[];
  kitchens: Kitchen[];
  printers: Printer[];
  expenseCategories: ExpenseCategory[];
}

async function loadAll(branchId: number): Promise<LoadedData> {
  // ยิงพร้อมกันทั้งหมด — ทีละตัวจะรอนานเกินไปตอนเปิดเครื่องตอนเช้า
  const [
    branches, products, productGroups, discountButtons, paymentMethods,
    deliveryChannels, tables, kitchens, printers, expenseCategories,
  ] = await Promise.all([
    api.get<{ branches: Branch[] }>('/branches').then((r) => r.branches),
    api.get<{ products: Product[] }>('/products').then((r) => r.products),
    api.get<{ productGroups: ProductGroup[] }>('/product-groups').then((r) => r.productGroups),
    api.get<{ discountButtons: DiscountButton[] }>('/discount-buttons').then((r) => r.discountButtons),
    api.get<{ paymentMethods: PaymentMethod[] }>('/payment-methods').then((r) => r.paymentMethods),
    api.get<{ deliveryChannels: DeliveryChannel[] }>('/delivery-channels').then((r) => r.deliveryChannels),
    api.get<{ tables: DiningTable[] }>('/tables', { branchId }).then((r) => r.tables),
    api.get<{ kitchens: Kitchen[] }>('/kitchens', { branchId }).then((r) => r.kitchens),
    api.get<{ printers: Printer[] }>('/printers', { branchId }).then((r) => r.printers),
    api.get<{ expenseCategories: ExpenseCategory[] }>('/expense-categories').then((r) => r.expenseCategories),
  ]);

  return {
    branches, products, productGroups, discountButtons, paymentMethods,
    deliveryChannels, tables, kitchens, printers, expenseCategories,
  };
}

export function StoreProvider({
  session,
  branchId,
  onFailure,
  children,
}: {
  session: Session;
  branchId: number;
  onFailure: (message: string) => void;
  children: ReactNode;
}) {
  const [data, setData] = useState<LoadedData | null>(null);
  const [drawer, setDrawer] = useState<DrawerSession | null>(null);

  const reloadDrawer = useCallback(async () => {
    const result = await api.get<DrawerSession | { open: false }>(
      '/drawer-sessions/current',
      { branchId },
    );
    setDrawer('open' in result && result.open === false ? null : (result as DrawerSession));
  }, [branchId]);

  const reload = useCallback(async () => {
    try {
      const [loaded] = await Promise.all([loadAll(branchId), reloadDrawer()]);
      setData(loaded);
    } catch (err) {
      onFailure(err instanceof Error ? err.message : 'โหลดข้อมูลไม่สำเร็จ');
    }
  }, [branchId, reloadDrawer, onFailure]);

  useEffect(() => { void reload(); }, [reload]);

  const store = useMemo<Store | null>(() => {
    if (!data) return null;
    const branch = data.branches.find((b) => b.id === branchId);
    if (!branch) return null;
    return { session, branch, ...data, drawer, reloadDrawer, reload };
  }, [data, drawer, branchId, session, reloadDrawer, reload]);

  if (!store) {
    return (
      <div className="center-screen">
        <div className="spinner" />
        <div>กำลังโหลดข้อมูลร้าน…</div>
      </div>
    );
  }
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

/** ติดตามการเข้า/ออกจากระบบจากที่ใดก็ได้ในแอป */
export function useSession(): Session | null {
  const [session, setSession] = useState<Session | null>(getSession());
  useEffect(() => onSessionChange(setSession), []);
  return session;
}
